import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const PermissionContext = createContext();

/**
 * Loads the current admin's profile + flattened permission list
 * (["users.view","events.create",...]) from GET /auth/me right after a
 * token shows up, and exposes hasPermission(module, action) for gating
 * routes, sidebar items and buttons.
 *
 * superAdmin always passes hasPermission — same bypass the backend's
 * checkPermission middleware uses, kept in sync deliberately: hiding UI
 * for superAdmin would just be wrong, not a security boundary (the
 * backend is the real gate either way).
 */
export const PermissionProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [permissions, setPermissions] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setProfile(null);
      setPermissions(new Set());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result?.status && result?.data) {
        setProfile(result.data);
        setPermissions(new Set(result.data.permissions || []));
      } else {
        setProfile(null);
        setPermissions(new Set());
      }
    } catch {
      setProfile(null);
      setPermissions(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const hasPermission = (module, action = "view") => {
    if (!module) return true; // ungated route/button
    if (profile?.role === "superAdmin") return true;
    return permissions.has(`${module}.${action}`);
  };

  return (
    <PermissionContext.Provider value={{ profile, loading, hasPermission, reload: load }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => useContext(PermissionContext);
